import { Branch, TrashCan } from "@carbon/icons-react";
import AssignmentInd from "@mui/icons-material/AssignmentInd";
import Commit from "@mui/icons-material/Commit";
import Dns from "@mui/icons-material/Dns";
import History from "@mui/icons-material/History";
import Merge from "@mui/icons-material/Merge";
import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import { type StoreApi, type UseBoundStore, create } from "zustand";
import { useShallow } from "zustand/shallow";

import { useIsFeatureFlagEnabled } from "~/state";
import { Flag } from "~/state/types";

export const HEAD_REFSPEC = "HEAD";

export const settings = [
	{ title: "Remotes", name: "remotes", url: "#", icon: Dns },
	{ title: "Branches", name: "branches", url: "#", icon: Branch },
	// { title: "Auto-sync", name: "autosync", url: "#", icon: AutoMode },
	{ title: "User settings", name: "user", url: "#", icon: AssignmentInd },
] as const;

export type Settings = (typeof settings)[number]["name"];

export interface SelectedSettings {
	selectedItem: "settings";
	selectedSection: Settings;
}

export interface SelectedCommit {
	selectedItem: "commit";
	workspaceName: string;
	documentId: string;
}

export interface SelectedSynchronize {
	selectedItem: "synchronize";
	workspaceName: string;
	documentId: string;
}

export interface SelectedHistory {
	selectedItem: "history";
	workspaceName: string;
	documentId: string;
	revision: string;
}

export interface SelectedNewBranch {
	selectedItem: "new-branch";
	workspaceName: string;
	documentId: string;
}

export interface SelectedMergeBranch {
	selectedItem: "merge-branch";
	workspaceName: string;
	documentId: string;
}

export interface SelectedDeleteBranch {
	selectedItem: "delete-branch";
	workspaceName: string;
	documentId: string;
}

type AllSelections =
	| SelectedSettings
	| SelectedCommit
	| SelectedSynchronize
	| SelectedHistory
	| SelectedNewBranch
	| SelectedMergeBranch
	| SelectedDeleteBranch;

export interface DocumentVersioningDialogState {
	isOpen: boolean;
	setIsOpen: (openSetter: boolean | ((open: boolean) => boolean)) => void;
	selection: AllSelections;
	setSelection: (
		item:
			| "commit"
			| "history"
			| "settings"
			| "new-branch"
			| "merge-branch"
			| "delete-branch"
			| (typeof actions)[number]
			| (typeof settings)[number]
			| undefined
	) => void;
	updateSelection: Dispatch<SetStateAction<AllSelections>>;
	isRootSidebarOpen: boolean;
	setIsRootSidebarOpen: (open: boolean) => void;
}

export const actions = [
	{
		title: "Diff/commit changes…",
		name: "commit",
		url: "#",
		icon: Commit,
		isActive: false,
		items: [],
	},
	// {
	// 	title: "Pull/push changes…",
	// 	name: "synchronize",
	// 	url: "#",
	// 	icon: SwapVerticalCircle,
	// 	isActive: false,
	// 	items: [],
	// },
	{
		title: "View commit history…",
		name: "history",
		url: "#",
		icon: History,
		isActive: false,
		items: [],
	},
	{
		title: "Create new branch...",
		name: "new-branch",
		url: "#",
		icon: Branch,
		isActive: false,
		items: [],
	},
	{
		title: "Merge a branch...",
		name: "merge-branch",
		url: "#",
		icon: Merge,
		isActive: false,
		items: [],
	},
	{
		title: "Delete a branch...",
		name: "delete-branch",
		url: "#",
		icon: TrashCan,
		isActive: false,
		items: [],
	},
] as const;

export type Action = (typeof actions)[number]["name"];

const useDocumentVersioningDialogStore: UseBoundStore<
	StoreApi<{
		selection: AllSelections;
		setSelection: (selection: AllSelections) => void;
		isOpen: boolean;
		setIsOpen: (open: boolean) => void;
		isRootSidebarOpen: boolean;
		setIsRootSidebarOpen: (open: boolean) => void;
	}>
> = create((set) => ({
	selection: {
		selectedItem: "settings",
		selectedSection: "remotes",
	} as AllSelections,
	setSelection: (selection: AllSelections | ((selection: AllSelections) => AllSelections)) =>
		set(({ selection: state }) => ({
			selection: typeof selection === "function" ? selection(state) : selection,
		})),

	isOpen: false,
	setIsOpen: (open: boolean) => set({ isOpen: open }),
	isRootSidebarOpen: true,
	setIsRootSidebarOpen: (open: boolean) => set({ isRootSidebarOpen: open }),
}));

export const useDocumentVersioningDialog = ({
	workspaceName,
	documentId,
}: {
	workspaceName: string;
	documentId: string;
}): DocumentVersioningDialogState => {
	const [selection, _setSelection] = useDocumentVersioningDialogStore(
		useShallow((store) => [store.selection, store.setSelection] as const)
	);
	const [isRootSidebarOpen, setIsRootSidebarOpen] = useDocumentVersioningDialogStore(
		useShallow((store) => [store.isRootSidebarOpen, store.setIsRootSidebarOpen] as const)
	);
	const [isOpen, _setIsOpen] = useDocumentVersioningDialogStore(
		useShallow((store) => [store.isOpen, store.setIsOpen] as const)
	);

	const gitBranchingEnabled = useIsFeatureFlagEnabled(Flag.GitBranching);

	const setSelection = useCallback(
		(
			item:
				| "commit"
				| "history"
				| "settings"
				| "new-branch"
				| "merge-branch"
				| "delete-branch"
				| (typeof actions)[number]
				| (typeof settings)[number]
				| undefined
		) => {
			if (item === "commit") {
				_setIsOpen(true);
				setIsRootSidebarOpen(false);
				_setSelection({ selectedItem: "commit", workspaceName, documentId });
				return;
			} else if (item === "history") {
				_setIsOpen(true);
				setIsRootSidebarOpen(false);
				_setSelection({
					selectedItem: "history",
					workspaceName,
					documentId,
					revision: HEAD_REFSPEC,
				});
				return;
			} else if (item === "new-branch") {
				if (gitBranchingEnabled) {
					_setIsOpen(true);
					setIsRootSidebarOpen(false);
					_setSelection({
						selectedItem: "new-branch",
						workspaceName,
						documentId,
					});
				}
				return;
			} else if (item === "merge-branch") {
				if (gitBranchingEnabled) {
					_setIsOpen(true);
					setIsRootSidebarOpen(true);
					_setSelection({
						selectedItem: "merge-branch",
						workspaceName,
						documentId,
					});
				}
				return;
			} else if (item === "delete-branch") {
				if (gitBranchingEnabled) {
					_setIsOpen(true);
					setIsRootSidebarOpen(false);
					_setSelection({
						selectedItem: "delete-branch",
						workspaceName,
						documentId,
					});
				}
				return;
			} else if (item === "settings") {
				_setIsOpen(true);
				setIsRootSidebarOpen(false);
				_setSelection({ selectedItem: "settings", selectedSection: "remotes" });
				return;
			}

			switch (item?.name) {
				case "branches":
				case "remotes":
				case "user":
					_setIsOpen(true);
					setIsRootSidebarOpen(false);
					_setSelection({ selectedItem: "settings", selectedSection: item.name });
					break;
				case "commit":
					_setIsOpen(true);
					setIsRootSidebarOpen(false);
					_setSelection({ selectedItem: "commit", workspaceName, documentId });
					break;
				case "new-branch":
					if (gitBranchingEnabled) {
						_setIsOpen(true);
						setIsRootSidebarOpen(false);
						_setSelection({ selectedItem: "new-branch", workspaceName, documentId });
					}
					break;
				case "merge-branch":
					if (gitBranchingEnabled) {
						_setIsOpen(true);
						setIsRootSidebarOpen(true);
						_setSelection({ selectedItem: "merge-branch", workspaceName, documentId });
					}
					break;
				case "delete-branch":
					if (gitBranchingEnabled) {
						_setIsOpen(true);
						setIsRootSidebarOpen(false);
						_setSelection({ selectedItem: "delete-branch", workspaceName, documentId });
					}
					break;
				// case "synchronize":
				// 	_setIsOpen(true);
				// 	setIsRootSidebarOpen(false);
				// 	_setSelection({ selectedItem: "synchronize", workspaceName, documentId });
				// 	break;
				case "history":
					_setIsOpen(true);
					setIsRootSidebarOpen(false);
					_setSelection({
						selectedItem: "history",
						workspaceName,
						documentId,
						revision: HEAD_REFSPEC,
					});
					break;
				case undefined:
					_setIsOpen(false);
					setIsRootSidebarOpen(true);
					_setSelection({ selectedItem: "settings", selectedSection: "remotes" });
					break;
				default:
					_setIsOpen(false);
					setIsRootSidebarOpen(true);
					_setSelection({ selectedItem: "settings", selectedSection: "remotes" });
					break;
			}
		},
		[
			_setIsOpen,
			setIsRootSidebarOpen,
			_setSelection,
			workspaceName,
			documentId,
			gitBranchingEnabled,
		]
	);

	const setIsOpen = useCallback(
		(openSetter: boolean | ((open: boolean) => boolean)) => {
			const openValue = typeof openSetter === "function" ? openSetter(isOpen) : openSetter;
			_setIsOpen(openValue);
			if (!openValue) {
				setSelection(undefined);
				setIsRootSidebarOpen(true);
			}
		},
		[isOpen, _setIsOpen, setSelection, setIsRootSidebarOpen]
	);

	const updateSelection: Dispatch<SetStateAction<AllSelections>> = useCallback(
		(selector) => {
			const newSelection = typeof selector === "function" ? selector(selection) : selection;
			return _setSelection(newSelection);
		},
		[_setSelection, selection]
	);

	return useMemo(
		() => ({
			isOpen,
			setIsOpen,
			selection,
			setSelection,
			updateSelection,
			isRootSidebarOpen,
			setIsRootSidebarOpen,
		}),
		[
			isOpen,
			setIsOpen,
			selection,
			setSelection,
			updateSelection,
			isRootSidebarOpen,
			setIsRootSidebarOpen,
		]
	);
};
